import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sun, Info, MapPin, Zap, AlertTriangle, Loader2, Clock, CalendarDays, LineChart as ChartIcon } from 'lucide-react';
import { fetchForecastSolarEstimate } from '@/api/forecastSolar';
import type { ForecastSolarEstimate } from '@/api/forecastSolar';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { useErrorToast, LoadRetry } from '@/lib/notify';

export default function ForecastSolarPage() {
  const { uuid_id } = useParams<{ uuid_id: string }>();

  const [data, setData] = useState<ForecastSolarEstimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  useEffect(() => {
    const loadData = async () => {
      if (!uuid_id) return;
      setLoading(true);
      try {
        const result = await fetchForecastSolarEstimate(uuid_id);
        setData(result);
      } catch (err: any) {
        console.error(err);
        setError(err.response?.data?.error || 'Veri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [uuid_id]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
          <p className="text-gray-500 font-medium">Tahmin verileri alınıyor...</p>
        </div>
      </div>
    );
  }

  if (error) return <LoadRetry className="h-full" />;

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-center max-w-md">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-lg font-semibold text-red-800">Veri bulunamadı</h2>

        </div>
      </div>
    );
  }

  // Transform hourly watts data for charts
  const hourlyWatts = Object.entries(data.forecast.result.watts).map(([time, watts]) => ({
    time,
    displayTime: time.split(' ')[1].substring(0, 5),
    date: time.split(' ')[0],
    watts
  }));

  // Group by date for daily summaries
  const dailyTotals = Object.entries(data.forecast.result.watt_hours_day).map(([date, energy]) => ({
    date,
    energy: energy / 1000 // Convert to kWh
  }));

  return (
    <div className="flex flex-col gap-6 overflow-y-auto flex-1 min-h-0 pb-6 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">

          <div className="flex flex-col">
            <h1 className="text-xl font-semibold text-slate-900">
              {data.plant.name} - Günlük Üretim Tahmini
            </h1>
            <div className="flex items-center gap-2">
               <p className="text-sm text-slate-500">Forecast.Solar API ile canlı hava durumuna dayalı üretim öngörüsü</p>
               <div className="flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-600">
                 <Clock className="h-3 w-3" />
                 {data.forecast.message.info.time} ({data.forecast.message.info.timezone})
               </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
              <Zap className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">Bugünkü Toplam Beklenti</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900">
              {(dailyTotals[0]?.energy || 0).toFixed(1)}
            </span>
            <span className="text-sm font-medium text-slate-400">kWh</span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-orange-50 p-2 text-orange-600">
              <Sun className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">Yarınki Toplam Beklenti</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900">
              {(dailyTotals[1]?.energy || 0).toFixed(1)}
            </span>
            <span className="text-sm font-medium text-slate-400">kWh</span>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
              <CalendarDays className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">Panel Parametreleri</span>
          </div>
          <div className="text-xs text-slate-600 font-bold space-y-1">
            <div className="flex justify-between"><span>Eğim (Tilt):</span> <span className="text-indigo-600">{data.plant.dec}°</span></div>
            <div className="flex justify-between"><span>Azimut:</span> <span className="text-indigo-600">{data.plant.az}°</span></div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="rounded-lg bg-slate-50 p-2 text-slate-600">
              <MapPin className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-slate-500">Konum ve Güç</span>
          </div>
          <div className="text-xs text-slate-600 font-bold">
            {Number(data.plant.lat).toFixed(4)}, {Number(data.plant.lon).toFixed(4)}
            <br />
            <span className="text-blue-600">{data.plant.peakpower.toLocaleString()} kWp Kurulu Güç</span>
          </div>
        </div>
      </div>

      {/* Hourly Chart */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <ChartIcon className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-slate-900">Saatlik Üretim Beklentisi (Watt)</h3>
        </div>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={hourlyWatts}>
              <defs>
                <linearGradient id="colorWatts" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis 
                dataKey="displayTime" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 10, fill: '#64748b' }}
                interval={4}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 11, fill: '#64748b' }}
              />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                labelFormatter={(label, items) => items[0]?.payload?.time || label}
              />
              <Area 
                type="monotone" 
                dataKey="watts" 
                stroke="#3b82f6" 
                fillOpacity={1} 
                fill="url(#colorWatts)" 
                strokeWidth={3}
                name="Anlık Güç"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Next Days Summary */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
         <h3 className="text-lg font-semibold text-slate-900 mb-6">Günlük Toplam Enerji Tahmini</h3>
         <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {dailyTotals.map((day, idx) => (
              <div key={idx} className="p-4 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col items-center gap-1 group hover:bg-blue-50 hover:border-blue-100 transition-all">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{new Date(day.date).toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                <span className="text-xl font-black text-slate-900 group-hover:text-blue-600 transition-colors">{day.energy.toFixed(1)} <span className="text-xs font-bold text-slate-500">kWh</span></span>
              </div>
            ))}
         </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-center gap-2 text-xs text-slate-400 bg-white/50 p-4 rounded-xl border border-dashed border-slate-200">
        <Info className="h-4 w-4" />
        <span>Bu veriler <b>Forecast.Solar</b> API'si kullanılarak üretilmiştir. Tahminler hava durumuna bağlı olarak değişebilir. Limit: {data.forecast.message.ratelimit.remaining}/{data.forecast.message.ratelimit.limit} (Kalan/Limit)</span>
      </div>
    </div>
  );
}
